/**
 * LE CONSENTEMENT COOKIES — l'état, et rien que l'état.
 *
 * POURQUOI CE MODULE EXISTE. Le site n'avait pas de bandeau (Notion, « Last push
 * before going live » → Landing → « Cookie pop up : to implement »), et ce
 * n'était pas seulement une case à cocher réglementaire manquante : le
 * messager **Intercom** était démarré pour TOUT visiteur, y compris anonyme,
 * depuis `App.tsx` (`IntercomProvider`, monté au-dessus de toutes les routes).
 * Intercom pose ses propres cookies tiers. Pendant ce temps la politique de
 * confidentialité affirmait, en trois langues, que « les cookies non essentiels
 * ne sont déposés qu'avec votre consentement ». Le produit disait une chose et
 * en faisait une autre ; c'est ce décalage-là que ce module ferme.
 *
 * LES TROIS CATÉGORIES.
 *   · `necessary` — le cookie `aikka_token` (session, 15 min, `secure`,
 *     `samesite=lax`, posé par `lib/auth.tsx`) et les préférences gardées en
 *     `localStorage` (`aikka-lang`, le thème). Sans eux le site ne fonctionne
 *     pas ou perd la langue à chaque page. Non désactivable, et le bandeau le
 *     dit plutôt que d'afficher un interrupteur bloqué.
 *   · `support` — Intercom. C'est un service tiers, donc un choix.
 *   · `analytics` — Hotjar (heatmaps et enregistrements de session). Arrivé
 *     après les deux autres, sur décision du 4 septembre 2026.
 *
 * `analytics` EST UNE CATÉGORIE SÉPARÉE, ET PAS UNE CASE DE PLUS DANS
 * `support`. Les deux services n'ont ni la même finalité ni le même degré
 * d'intrusion : Intercom attend qu'on lui écrive, Hotjar filme la page. Un
 * visiteur qui veut pouvoir demander de l'aide sans être enregistré doit
 * pouvoir le dire, et une case unique le lui refuserait.
 *
 * LE STOCKAGE. `localStorage`, pas un cookie : un cookie de consentement serait
 * lui-même un cookie posé avant consentement, et la valeur n'a aucune raison de
 * voyager vers le serveur. La clé porte une VERSION — rouvrir le bandeau après
 * l'ajout d'une catégorie est une obligation, et sans version il n'y a pas de
 * moyen de le faire sans effacer le choix de tout le monde à chaque déploiement.
 */

export type ConsentCategory = 'necessary' | 'support' | 'analytics';

export interface CookieConsent {
  /** Toujours `true` — présent pour que la forme de l'objet soit lisible. */
  necessary: true;
  /** Intercom. `false` tant que le visiteur n'a pas dit oui. */
  support: boolean;
  /**
   * Mesure d'audience : Contentsquare (Hotjar), les cookies de Google Analytics 4
   * (Consent Mode `analytics_storage`) et, sur /agent, le tag LinkedIn Insight.
   * `false` tant que le visiteur n'a pas dit oui.
   */
  analytics: boolean;
  /** ISO 8601, pour pouvoir prouver la date du choix. */
  decidedAt: string;
  /** Version du jeu de catégories au moment du choix. */
  version: number;
}

/**
 * À incrémenter dès qu'une catégorie entre ou sort : rouvre le bandeau.
 *
 * 1 → 2 : arrivée de `analytics` (Hotjar). Le bump REDEMANDE son consentement à
 * tout visiteur qui avait déjà tranché, et c'est le but — un « oui » donné à un
 * bandeau qui ne parlait que d'Intercom ne vaut pas accord pour être filmé. Le
 * seed E2E (`e2e/auth.setup.ts`) écrit cette version en dur : la faire suivre
 * fait partie du bump, sans quoi toute la suite se prend le bandeau.
 *
 * 2 → 3 : la catégorie `analytics` ouvrait déjà plus que ce que le bandeau en
 * disait. Sa description ne nommait que Contentsquare, alors qu'un « oui »
 * autorisait aussi les cookies de Google Analytics 4 et, sur /agent, le tag
 * LinkedIn Insight. La description les nomme désormais ; un accord donné à
 * l'ancienne description n'était pas éclairé sur ces deux-là, d'où la nouvelle
 * question à tout visiteur qui avait déjà tranché.
 */
export const CONSENT_VERSION = 3;

const STORAGE_KEY = 'aikka-cookie-consent';

/** Aucun choix mémorisé ⇒ rien de non essentiel n'est démarré. */
export const DENY_ALL: Omit<CookieConsent, 'decidedAt' | 'version'> = {
  necessary: true,
  support: false,
  analytics: false,
};

type Listener = (consent: CookieConsent | null) => void;
const listeners = new Set<Listener>();

/**
 * Lit le choix mémorisé, ou `null` s'il n'y en a pas — ce qui est aussi la
 * réponse quand `localStorage` est indisponible (navigation privée verrouillée,
 * site data bloqué) ou quand la version a changé. `null` fait s'ouvrir le
 * bandeau et laisse tout le non-essentiel éteint : le défaut sûr.
 */
export function readConsent(): CookieConsent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookieConsent>;
    if (parsed?.version !== CONSENT_VERSION) return null;
    return {
      necessary: true,
      support: parsed.support === true,
      analytics: parsed.analytics === true,
      decidedAt: typeof parsed.decidedAt === 'string' ? parsed.decidedAt : new Date().toISOString(),
      version: CONSENT_VERSION,
    };
  } catch {
    return null;
  }
}

/** Enregistre un choix et prévient les abonnés (les providers Intercom et Hotjar). */
export function writeConsent(choice: { support: boolean; analytics: boolean }): CookieConsent {
  const consent: CookieConsent = {
    necessary: true,
    support: choice.support,
    analytics: choice.analytics,
    decidedAt: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    /* stockage indisponible : le choix vaut pour la session en cours, via les
       abonnés ci-dessous. Il ne survivra pas au rechargement, et le bandeau
       reviendra — c'est le comportement honnête, pas un bug à masquer. */
  }
  listeners.forEach((fn) => fn(consent));
  return consent;
}

/**
 * Efface le choix : le bandeau se rouvre au prochain rendu. C'est ce que fait
 * le lien « Gérer les cookies » du pied de page et de la page /cookies — le RGPD
 * demande que retirer son consentement soit aussi simple que le donner.
 */
export function resetConsent(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* voir writeConsent */
  }
  listeners.forEach((fn) => fn(null));
}

/** S'abonner aux changements. Renvoie la fonction de désabonnement. */
export function subscribeConsent(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
